/* eslint-disable react/prop-types */
import React from 'react';
import { ScrollToTop } from 'component';
import {
  Route,
  Routes,
  unstable_HistoryRouter as HistoryRouter
} from 'react-router-dom';
import { jitsiLocalStorage } from '@jitsi/js-utils';
import { history } from 'CustomHistory';
import { appWillMount, appWillUnmount } from 'store/modules/base/app';
import { connect } from 'store/modules/base/redux';
import { loadInitialConfig } from 'store/modules/app/actions';
import { shouldDisplayNotifications } from 'store/modules/conference/functions';
import CircularProgress from '@mui/material/CircularProgress';
import Conference from 'modules/Conference/Conference';
import LogIn from 'modules/LogIn/Login';
import Home from 'modules/LogIn/Home';
import ActivationCode from 'modules/LogIn/ActivationCode';
import EmailVerification from 'modules/LogIn/EmailVerification';
import EmailAuthentication from 'modules/LogIn/EmailAuthentication';
import Settings from 'modules/LogIn/Settings';
import SoundCollection from 'modules/Conference/components/Sounds/SoundCollection';
import DialogContainer from 'modules/Conference/components/Dialogs/DialogContainer';
import NotificationsContainer from 'modules/Conference/components/Notifications/NotificationsContainer';
import OverlayContainer from 'modules/Conference/components/Overlay/OverlayContainer';
import AutoUpdate from 'component/AutoUpdate/AutoUpdate';
import withBoundary from 'component/ErrorBoundary/withBoundary';
// Register middlewares and reducers.
import 'store/modules/app/reducers';
import 'store/modules/app/middlewares';

class App extends React.Component {
  /**
   * Initializes a new {@code BaseApp} instance.
   *
   * @param {Object} props - The read-only React {@code Component} props with
   * which the new instance is to be initialized.
   */
  constructor(props) {
    super(props);
  }

  /**
   * Initializes the app.
   *
   * @inheritdoc
   */
  componentDidMount() {
    /**
     * Make the mobile {@code BaseApp} wait until the {@code AsyncStorage}
     * implementation of {@code Storage} initializes fully.
     *
     * @private
     * @see {@link #_initStorage}
     * @type {Promise}
     */

    try {
      this._initStorage();
    } catch (err) {
      /* BaseApp should always initialize! */
      console.error(err);
    }

    this.props.dispatch(loadInitialConfig());

    this.props.dispatch(appWillMount(true));
  }

  /**
   * De-initializes the app.
   *
   * @inheritdoc
   */
  componentWillUnmount() {
    this.props.dispatch(appWillUnmount(this));
  }

  /**
   * Logs for errors that were not caught.
   *
   * @param {Error} error - The error that was thrown.
   * @param {Object} info - Info about the error(stack trace);.
   *
   * @returns {void}
   */
  componentDidCatch(error, info) {
    console.error(error, info);
  }

  /**
   * Delays this {@code BaseApp}'s startup until the {@code Storage}
   * implementation of {@code localStorage} initializes. While the
   * initialization is instantaneous on Web (with Web Storage API), it is
   * asynchronous on mobile/react-native.
   *
   * @private
   * @returns {Promise}
   */
  _initStorage() {
    const _initializing = jitsiLocalStorage.getItem('_initializing');
    return _initializing || Promise.resolve();
  }

  render() {
    const { _loading, _notificationsVisible } = this.props;
    if (_loading) return <CircularProgress size={15} />;
    return (
      <div className="App">
        <HistoryRouter history={history}>
          <ScrollToTop />
          <Routes>
            <Route path="*" element={<LogIn />} />
            <Route path="/groups" element={<Home />} />
            <Route
              path="/conference"
              element={<Conference />}
              key={history.location.key}
            />

            <Route path="/activation-code" element={<ActivationCode />} />
            <Route path="/email-verification" element={<EmailVerification />} />
            <Route
              path="/email-authentication"
              element={<EmailAuthentication />}
            />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </HistoryRouter>
        <SoundCollection />
        <DialogContainer />
        <OverlayContainer />
        {_notificationsVisible && <NotificationsContainer />}
        <AutoUpdate />
      </div>
    );
  }
}
/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code Conference} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state) {
  return {
    _notificationsVisible: shouldDisplayNotifications(state),
    _loading: state['features/login'].loading
  };
}
export default React.memo(connect(_mapStateToProps)(withBoundary(App)));
